import { isFavorite, mapToMatchSmallSmall, sameDay, shouldAdBePlaced, sortMatchesByTimeFast } from "./utils";
import { HomeMatch, MatchType, TournamentMinimal, Translation } from "../client/api";
import { Favorite, IDType, MatchSmallElement } from "./types";

export declare type HomeMatchSmallElement = MatchSmallElement & {
  isFavorite?: boolean;
  isFavoriteTeam?: boolean;
  showLeague?: boolean;
  key?: string;
};
export declare type HomeSectionTitle = {
  name: Translation;
  id: IDType | undefined;
  type: MatchType;
};
export declare type HomeItem =
  | HomeSectionTitle
  | string
  | TournamentMinimal
  | HomeMatchSmallElement;

export function notFoundButInBetween(dates: Date[], add: Date): boolean {
  let found = false;
  let min = Infinity;
  let max = 0;
  dates.forEach((day) => {
    if (sameDay(day, add)) {
      found = true;
    }
    if (day.getTime() > max) {
      max = day.getTime();
    }
    if (day.getTime() < min) {
      min = day.getTime();
    }
  });
  return !found && add.getTime() < max && add.getTime() > min;
}

export function addMatchDays(
  matchDays: Date[],
  addDate: string,
  removeDate: string | null
) {
  const isSameDay = addDate === removeDate;
  if (isSameDay) {
    return matchDays;
  }
  const add = new Date(addDate);
  if (notFoundButInBetween(matchDays, add)) {
    return matchDays.concat([add]).sort((a, b) => a.getTime() - b.getTime());
  }
  return matchDays;
}


export const CUP_ID = -2124;
export const FAVORITE_ID = -21232;

function generateAds(items: HomeItem[]): HomeItem[] {
  let matchCounter = 0;
  const idxs: number[] = [];
  items.forEach((m, i) => {
    if (typeof m !== "string") {
      matchCounter++;
      if (shouldAdBePlaced(matchCounter, idxs.length)) {
        idxs.push(i);
        matchCounter = 0;
      }
    }
  });
  return items.reduce<HomeItem[]>((prev, cur, currentIndex) => {
    const adIdx = idxs.indexOf(currentIndex - 1);
    if (adIdx !== -1) {
      return [
        ...prev,
        cur,
        `home-ad-${adIdx}-${currentIndex}-${
          adIdx === 0 ? "priority" : "normal"
        }`
      ];
    }
    return [...prev, cur];
  }, []);
}

export function generateHomeSections(
  matches: HomeMatch[],
  cups: TournamentMinimal[],
  favorites: Favorite[]
): HomeItem[] {
  const favoriteMatches: HomeItem[] = [];
  const regularLeagues: HomeItem[] = [];
  const favoriteLeagues: HomeItem[] = [];
  const cupSection: HomeItem[] =
    cups && cups.length !== 0
      ? [
        {
          name: {
            id: CUP_ID,
            textDE: "Turnier",
            textIT: "Torneo",
            textEN: "Cup"
          },
          id: -1,
          type: MatchType.TOURNAMENT
        } as HomeItem
      ].concat(cups as HomeItem[])
      : [];
  matches.forEach(leagueMatch => {
    const temp: HomeItem = {
      name: leagueMatch.leagueTournamentName,
      id: leagueMatch.leagueTournamentId,
      type: leagueMatch.leagueTournamentType
    };
    const tempData: HomeItem[] = [];
    let curLeague = false;
    leagueMatch.matches
      .map(mapToMatchSmallSmall)
      .sort(sortMatchesByTimeFast)
      .forEach(match => {
        const ft = isFavorite(favorites, match.team1.id, "team");
        const ft2 = isFavorite(favorites, match.team2.id, "team");
        const fm = isFavorite(favorites, match.id, "match");
        if (ft || ft2 || fm) {
          if (curLeague) {
            favoriteMatches.push({
              ...match,
              isFavorite: true,
              isFavoriteTeam: (ft || ft2) && !fm,
              showLeague: false,
              key: "fav"
            });
          } else {
            curLeague = true;
            favoriteMatches.push({
              ...match,
              isFavorite: true,
              isFavoriteTeam: (ft || ft2) && !fm,
              showLeague: true,
              key: "fav"
            });
          }
        }
        tempData.push({
          ...match,
          isFavorite: ft || ft2 || fm,
          isFavoriteTeam: (ft || ft2) && !fm,
          showLeague: false,
          key: "not-fav"
        });
      });
    if (tempData.length !== 0) {
      if (leagueMatch.leagueTournamentType === MatchType.LEAGUE &&
        isFavorite(
          favorites,
          leagueMatch.leagueTournamentId,
          "league",
          -1,
          -1
        )
      ) {
        favoriteLeagues.push(temp);
        tempData.forEach(match => {
          favoriteLeagues.push(match);
        });
      } else {
        regularLeagues.push(temp);
        tempData.forEach(match => {
          regularLeagues.push(match);
        });
      }
    }
  });

  const retval: HomeItem[] = (
    favoriteMatches.length !== 0
      ? [
        {
          name: {
            id: FAVORITE_ID,
            textDE: "Favoriten",
            textIT: "Preferiti",
            textEN: "Favorites"
          },
          id: undefined,
          type: MatchType.LEAGUE
        } as HomeItem
      ].concat(favoriteMatches)
      : []
  )
    .concat(cupSection)
    .concat(favoriteLeagues)
    .concat(regularLeagues);

  return generateAds(retval);
}
